import { useState } from "react";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";

import {
  Box,
  Button,
  IconButton,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CloseSVGIcon,
  LockIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { fetchCurrentUser } from "@js/apps/auth/actions";
import { useLazyGetActiveScreeningQuery } from "@js/apps/auth/api";
import { useAccountType } from "@js/apps/common/hooks/use-account-type";
import { useUser } from "@js/apps/common/hooks/use-user";
import { updateFreelancerProfile } from "@js/apps/freelancer/actions";
import { freelancerApi } from "@js/apps/freelancer/api";
import { openIdentityVerificationModal } from "@js/apps/identity-verification/components/identity-verification-modal";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";
import { RhfTextField } from "@js/rhf/fields";

import {
  VerifyIdentityModalBottomSection,
  VerifyIdentityModalTopSection,
} from "../verify-identity-modal-content";

import styles from "./styles.module.scss";

const STEP = {
  INTRO: "intro",
  CONFIRM_NAME: "confirm-name",
  SKIP: "skip",
} as const;

type Step = ObjectValues<typeof STEP>;

type StartIdentityVerificationModalContentProps = {
  closeModal: () => void;
  onVerificationSuccess?: () => void;
  onVerificationFail?: () => void;
};

export const StartIdentityVerificationModalContent = ({
  closeModal,
  onVerificationSuccess,
  onVerificationFail,
}: StartIdentityVerificationModalContentProps) => {
  const { isFreelancerApproved } = useAccountType();
  const [getActiveScreening] = useLazyGetActiveScreeningQuery();
  const [step, setStep] = useState<Step>(STEP.INTRO);

  const handleVerificationSuccess = async () => {
    if (isFreelancerApproved) {
      onVerificationSuccess?.();
      return;
    }

    try {
      const data = await getActiveScreening().unwrap();
      if (!data.screening_url) {
        throw new Error("Screening url unavailable");
      }

      // open AIR screening
      window.location.href = data.screening_url;
    } catch (error) {
      Sentry.captureException("BidIdentityVerificationSuccess", {
        extra: { error },
      });
      Snackbar.error("Something went wrong!");
    }
  };

  const launchPersonaVerification = () => {
    closeModal();
    openIdentityVerificationModal({
      onVerificationSuccess: async () => {
        void handleVerificationSuccess();
      },
      onVerificationFail,
      onVerificationClose: onVerificationFail,
    });
  };

  const handleClose = () => {
    closeModal();
    onVerificationFail?.();
  };

  if (step === STEP.SKIP) {
    return (
      <IdentityVerificationSkippedContent
        onClose={handleClose}
        onVerifyIdentityClick={() => setStep(STEP.CONFIRM_NAME)}
      />
    );
  }

  if (step === STEP.CONFIRM_NAME) {
    return (
      <ConfirmLegalNameStep
        onConfirm={launchPersonaVerification}
        onClose={handleClose}
      />
    );
  }

  return (
    <Stack sx={{ width: "min(520px, 100vw - 2rem)" }}>
      <VerifyIdentityModalTopSection
        onSkip={() => setStep(STEP.SKIP)}
        title={
          <>
            Verify your identity to unlock
            <br />
            client review.
          </>
        }
        subtitle="REQUIRED TO BE HIRED"
      />
      <VerifyIdentityModalBottomSection
        onVerifyIdentityClick={() => setStep(STEP.CONFIRM_NAME)}
      />
    </Stack>
  );
};

type ConfirmLegalNameFormValues = {
  firstName: string;
  lastName: string;
};

type ConfirmLegalNameStepProps = {
  onConfirm: () => void;
  onClose: () => void;
};

const ConfirmLegalNameStep = ({
  onConfirm,
  onClose,
}: ConfirmLegalNameStepProps) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const freelancerId = user?.freelancer;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ConfirmLegalNameFormValues>({
    defaultValues: {
      firstName: user?.first_name ?? "",
      lastName: user?.last_name ?? "",
    },
  });

  const onSubmit = async (data: ConfirmLegalNameFormValues) => {
    if (!freelancerId) {
      return;
    }
    const hasChanged =
      data.firstName !== user?.first_name || data.lastName !== user?.last_name;

    if (hasChanged) {
      try {
        await dispatch(
          updateFreelancerProfile({
            freelancerId: freelancerId,
            updatedValues: {
              user: { first_name: data.firstName, last_name: data.lastName },
            },
          }),
        );

        void dispatch(
          freelancerApi.util.invalidateTags(["FreelancerPublicProfile"]),
        );
        void dispatch(fetchCurrentUser());
      } catch {
        Snackbar.error("Failed to save your name. Please try again.");
        return;
      }
    }

    onConfirm();
  };

  return (
    <Stack
      sx={{
        width: "min(520px, 100vw - 2rem)",
        p: { xs: 3, sm: 4 },
        position: "relative",
      }}
    >
      <IconButton
        aria-label="close modal"
        size="medium"
        variant="gray-violet"
        shape="squared"
        sx={{
          position: "absolute",
          top: "18px",
          right: "18px",
          svg: { margin: 0 },
        }}
        onClick={onClose}
      >
        <CloseSVGIcon />
      </IconButton>
      <Typography
        component="h2"
        variant="title"
        size="small"
        fontWeight={500}
        sx={{ mb: 3, pr: 6 }}
      >
        Confirm your legal name
      </Typography>
      <Typography
        component="p"
        variant="paragraph"
        color="grey-1"
        sx={{ mb: 3 }}
      >
        Enter your name exactly as it appears on your ID.
        <br />
        We'll compare this name to your government-issued ID during
        verification.
      </Typography>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={{ gap: 4 }}>
        <Stack direction="row" sx={{ gap: 2 }}>
          <RhfTextField
            id="firstName"
            name="firstName"
            control={control}
            label="Legal first name"
            rules={{ required: "First name is required" }}
            sx={{ flex: 1 }}
          />
          <RhfTextField
            id="lastName"
            name="lastName"
            control={control}
            label="Legal last name"
            rules={{ required: "Last name is required" }}
            sx={{ flex: 1 }}
          />
        </Stack>
        <Button
          type="submit"
          variant="dark-violet"
          shape="squared"
          disabled={isSubmitting}
          sx={{
            width: "100%",
            boxShadow: "0px 4px 12px 0px #5D197440 !important",
            svg: { color: "var(--white)" },
          }}
          disabledType="no-change"
          endIcon={isSubmitting ? <Loader size={20} /> : undefined}
        >
          {isSubmitting ? "Saving..." : "Continue"}
        </Button>
      </Stack>
    </Stack>
  );
};

type IdentityVerificationSkippedContentProps = {
  onClose: () => void;
  onVerifyIdentityClick: () => void;
};

const IdentityVerificationSkippedContent = ({
  onClose,
  onVerifyIdentityClick,
}: IdentityVerificationSkippedContentProps) => {
  return (
    <Stack sx={{ width: "min(520px, 100vw - 2rem)" }}>
      <Stack
        sx={{
          p: { xs: 3, sm: 4.5 },
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <IconButton
          aria-label="close modal"
          size="medium"
          variant="gray-violet"
          shape="squared"
          sx={{
            position: "absolute",
            top: "18px",
            right: "18px",
            svg: { margin: 0 },
          }}
          onClick={onClose}
        >
          <CloseSVGIcon />
        </IconButton>
        <Box className={styles.lockContainer}>
          <LockIcon sx={{ color: "var(--dark-violet)", fontSize: "40px" }} />
        </Box>
        <Typography
          component="h2"
          variant="title"
          size="small"
          fontWeight={500}
          sx={{ mb: 2 }}
        >
          Skip identity verification and freeze application?
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          color="grey-1"
          sx={{ mb: 1.5 }}
        >
          You can skip this step for now, but your{" "}
          <Box component="span" sx={{ textDecoration: "underline" }}>
            application will not be considered
          </Box>{" "}
          until you complete identity verification.
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          color="grey-2"
          size="small"
          sx={{ mb: 3 }}
        >
          It takes about 30 seconds and you only do it once.
        </Typography>
        <Button
          variant="dark-violet"
          shape="squared"
          onClick={onVerifyIdentityClick}
          sx={{
            width: "100%",
            boxShadow: "0px 4px 12px 0px #5D197440 !important",
          }}
          size="medium"
        >
          Verify identity
        </Button>
      </Stack>
    </Stack>
  );
};

export const openStartIdentityVerificationModal = () => {
  ModalInstance.open({
    children: (
      <StartIdentityVerificationModalContent
        closeModal={() => ModalInstance.close()}
      />
    ),
    padding: false,
    className: styles.modal,
    closeButton: false,
  });
};
